<template>
  <button
    class="BallButton"
    type="button"
    :aria-pressed="isSelected"
    :disabled="isDisabled"
  >
    <Transition>
      <svg
        v-if="isSelected"
        class="BallButton__svg"
        width="108"
        height="108"
        viewBox="0 0 108 108"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <template v-if="value === 1">
          <rect
            width="108"
            height="108"
            fill="var(--colorOrange)"
          />
        </template>
        <template v-else-if="value === 0">
          <rect
            width="108"
            height="108"
            fill="var(--themeColorNeutral200)"
          />
        </template>
        <template v-else>
          <rect
            width="54"
            height="108"
            fill="var(--colorOrange)"
          />
          <rect
            x="54"
            width="54"
            height="108"
            fill="var(--themeColorNeutral200)"
          />
        </template>
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M52.0262 108L52.5 -0.5L54.5 -0.491266L54.0262 108.009L52.0262 108Z"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M0.5 53H107.5V55H0.5V53Z"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M-1.5 96C21.696 96 40.5 77.196 40.5 54C40.5 30.804 21.696 12 -1.5 12C-24.696 12 -43.5 30.804 -43.5 54C-43.5 77.196 -24.696 96 -1.5 96ZM-1.5 98C22.8005 98 42.5 78.3005 42.5 54C42.5 29.6995 22.8005 10 -1.5 10C-25.8005 10 -45.5 29.6995 -45.5 54C-45.5 78.3005 -25.8005 98 -1.5 98Z"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M108.5 96C131.696 96 150.5 77.196 150.5 54C150.5 30.804 131.696 12 108.5 12C85.304 12 66.5 30.804 66.5 54C66.5 77.196 85.304 96 108.5 96ZM108.5 98C132.801 98 152.5 78.3005 152.5 54C152.5 29.6995 132.801 10 108.5 10C84.1995 10 64.5 29.6995 64.5 54C64.5 78.3005 84.1995 98 108.5 98Z"
        />
      </svg>
    </Transition>
    <div class="BallButton__labelContainer">
      <Transition>
        <span
          v-if="!isSelected"
          class="BallButton__label--isNotSelected BallButton__label"
        >{{ label }}</span>
        <span
          v-else
          class="BallButton__label--isSelected BallButton__label"
        >{{ label }}</span>
      </Transition>
    </div>
  </button>
</template>

<script lang="ts" setup>
const {
  isSelected = false,
} = defineProps<{
  label: string,
  value: number,
  isSelected?: boolean,
  isDisabled?: boolean,
}>();
</script>

<style lang="scss" scoped>
.BallButton {
  --transitionDuration: 500ms;
  --transitionEasing: var(--easeOutBack);

  position: relative;
  isolation: isolate;

  width: 100%;
  max-width: 108px;
  aspect-ratio: 1;
  padding-top: 2px;
  container-type: inline-size;
  container-name: ballButton;

  color: var(--themeColorNeutral0);

  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);
  text-transform: uppercase;
  line-height: 1;

  transition: var(--transitionDuration) var(--transitionEasing);
  transition-property: filter, opacity;

  &::before {
    position: absolute;
    inset: 6px;

    border-radius: 50%;
    background-color: var(--themeColorNeutral900);

    transition: var(--transitionDuration) var(--transitionEasing);
    transition-property: opacity, transform;
    content: "";
  }

  &::after {
    position: absolute;
    inset: 0;

    border: solid 4px var(--themeColorNeutral900);
    border-radius: 50%;

    transition: var(--transitionDuration) var(--transitionEasing);
    transition-property: opacity, transform;
    content: "";
  }

  &:disabled {
    opacity: 0.6;
    filter: grayscale(100%);
  }

  &[aria-pressed="true"]::before {
    transform: scale(calc(108 / 96));
    opacity: 0;
  }

  &[aria-pressed="true"]::after {
    opacity: 0;
  }
}

.BallButton__svg {
  position: absolute;
  top: 0;
  left: 0;

  width: 100%;
  height: 100%;
  overflow: hidden;

  border-radius: 50%;
  outline: 2px solid var(--themeColorNeutral900);
  outline-offset: -2px;

  transition: var(--transitionDuration) var(--transitionEasing);
  transition-property: opacity, transform;

  path {
    fill: var(--themeColorNeutral900);
  }

  &.v-enter-from,
  &.v-leave-to {
    transform: scale(calc(96 / 108));
    opacity: 0;
  }
}

.BallButton__labelContainer {
  position: relative;
  display: grid;
  place-items: center;
}

.BallButton__label {
  position: absolute;

  @supports (font-size: round(19cqi, 1px)) {
    // stylelint-disable-next-line declaration-property-value-no-unknown
    font-size: round(19cqi, 1px);
  }

  @supports not (font-size: round(19cqi, 1px)) {
    // stylelint-disable-next-line declaration-property-value-no-unknown
    font-size: 19cqi;
  }

  transition: var(--transitionDuration) var(--transitionEasing);
  transition-property: opacity, transform;

  &.v-enter-from,
  &.v-leave-to {
    transform: scale(0.5);
    opacity: 0;
  }
}

.BallButton__label--isNotSelected {
  color: var(--themeColorNeutral0);
}

.BallButton__label--isSelected {
  position: relative;
  isolation: isolate;
  color: var(--themeColorNeutral900);

  &::before {
    position: absolute;
    inset: -5px -8px -3px;
    z-index: -1;

    border-radius: 9999px;
    background-color: var(--themeColorNeutral0);

    content: "";
  }
}
</style>
